/* PageHeader.jsx — Unified page header primitive.
 *
 * iter-alignment · P0 · ChainFlow · Unified Page Header
 * (2026-03-01) — one shared header block used across every
 * primary ChainFlow workspace/list page so navigating between
 * them keeps the icon / title / description visually anchored
 * to the same X/Y position, size and hierarchy.  Only the
 * page-specific content + control cluster on the right may
 * change.
 *
 * Presentation only — the primitive takes no data, dispatches
 * no events and reads no state.  Each page owns its own actions
 * slot and control behaviour.
 *
 * Slots:
 *   icon        · lucide-react icon component (e.g. HomeIcon).
 *                 Rendered inside a 32×32 rounded violet chip.
 *   title       · 28px semibold page title.
 *   description · 14px muted slate description directly beneath.
 *   actions     · optional right-aligned cluster (any JSX).
 *   testid      · required data-testid for the outer <header>.
 *   titleTestid · optional override for the <h1> testid; defaults
 *                 to `${testid}-title` so per-page contract locks
 *                 keep resolving without invention.
 *   descriptionTestid · optional override for the <p> testid.
 *   iconTone    · optional violet | slate override; defaults to
 *                 the canonical violet chip used by Deal Rooms.
 */
import React from "react";

/* iter-alignment · P0 · ChainFlow · Unified Page Header Position
 * Fix (2026-03-01) — Every primary page must render its content
 * inside this canonical shell so the shared <PageHeader> begins
 * at the same X/Y position across every route.  Copied verbatim
 * from Deal Rooms (the canonical reference) — do NOT drift or
 * override on a per-page basis.  Right-side actions still belong
 * to each page.
 *
 * Consumed by:
 *   • DealRoomsIndexPage.jsx  (canonical)
 *   • TodayPage.jsx           (Command Centre)
 *   • CommunicationCentrePage.jsx
 *   • SalesProgressionForecastPage.jsx
 *   • ForecastingCentrePage.jsx
 *   • DocumentsPage.jsx
 *   • PropertiesPage.jsx
 *   • ContactsPage.jsx
 *   • MissionControlPage.jsx  (Chains)
 *   • AIInsightsPage.jsx
 *
 * Individual Deal Room / property record pages keep their own
 * specialised property-hero containers per the acceptance spec.
 */
export const WORKSPACE_SHELL_CLASS =
  "max-w-[1400px] mx-auto px-4 md:px-6 xl:px-8 py-6 w-full";

const _tone = {
  violet: "bg-violet-50 text-violet-600",
  slate:  "bg-slate-100 text-slate-600",
};

export function PageHeader({
  icon: Icon,
  title,
  description,
  actions = null,
  testid,
  titleTestid,
  descriptionTestid,
  iconTone = "violet",
  className = "",
}) {
  const toneClass = _tone[iconTone] || _tone.violet;
  return (
    <header
      className={`flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between mb-6 ${className}`}
      data-testid={testid}
      data-cf-page-header="1"
    >
      <div className="flex items-start gap-3 min-w-0">
        {Icon ? (
          <div
            className={`h-8 w-8 rounded-lg ${toneClass} flex items-center justify-center shrink-0`}
            data-testid={testid ? `${testid}-icon` : undefined}
            aria-hidden
          >
            <Icon className="h-4 w-4" strokeWidth={2.2} />
          </div>
        ) : null}
        <div className="min-w-0">
          <h1
            className="text-[28px] font-semibold text-slate-900 tracking-[-0.015em] leading-tight"
            data-testid={titleTestid || (testid ? `${testid}-title` : undefined)}
          >
            {title}
          </h1>
          {description ? (
            <p
              className="text-[14px] text-slate-500 mt-1 leading-snug"
              data-testid={descriptionTestid || (testid ? `${testid}-description` : undefined)}
            >
              {description}
            </p>
          ) : null}
        </div>
      </div>
      {actions ? (
        <div
          className="flex items-center gap-2.5 flex-wrap shrink-0"
          data-testid={testid ? `${testid}-actions` : undefined}
        >
          {actions}
        </div>
      ) : null}
    </header>
  );
}

export default PageHeader;
